<template>
  <vonap-window
    transition="dialog-scale-transition"
    title="Drawdown"
    width="920px"
    height="600px"
    :modal="true"
    @success="refresh"
    @close="close"
    :visible="value"
  >
    <v-layout row justify-center>
      <v-form @submit.prevent="sendMail" ref="form">
        <v-card class="mx-auto">
          <v-container fluid grid-list-sm class="py-2">
            <v-layout row wrap style="height:50px;padding-top:8px">
              <v-flex xs6>
                <vonap-toolbar-label label="Drawdown Date:">
                  <kendo-datepicker
                    v-model="asOfDate"
                    :format="'yyyy-MM-dd'"
                    style="width: 125px;display:inline-block;"
                  />
                </vonap-toolbar-label>
              </v-flex>
              <v-flex xs6>
                <vonap-toolbar-label label="Template">
                  <kendo-dropdownlist
                    v-model="templateId"
                    :data-source="templateDataSource"
                    :data-text-field="'text'"
                    :data-value-field="'value'"
                    style="width:92px"
                  >
                  </kendo-dropdownlist>
                </vonap-toolbar-label>
              </v-flex>
            </v-layout>
            <kendo-grid :data-source="dealDataSource" :height="200" :editable="true" @save="onSave">
              <kendo-grid-column
                field="operationId"
                title="Trade Id"
                :width="80"
              ></kendo-grid-column>
              <kendo-grid-column field="description" title="Description"></kendo-grid-column>
              <kendo-grid-column
                width="150px"
                field="amount"
                title="Drawdown Amount"
                format="{0:n2}"
                :editor="amountEditor"
                :template="amountTemplate"
              ></kendo-grid-column>
              <kendo-grid-column
                width="100px"
                field="complete"
                title="Full/Partial"
                template="#= amount == leftToSettle ? 'Full' : 'Partial' # "
              >
              </kendo-grid-column>
            </kendo-grid>
            <v-text-field v-model="clientName" label="Client Name" readonly></v-text-field>
            <v-text-field
              v-model="sendFrom"
              type="email"
              label="From:"
              required
              @change="onSendFromChange"
              :rules="[v => !!v || 'Email is required!']"
            ></v-text-field>
            <div style="display:flex">
              <v-text-field
                v-model="sendTo"
                type="email"
                label="To:"
                required
                :rules="[v => !!v || 'Email is required!']"
              ></v-text-field>
            </div>
            <div style="display:flex">
              <v-text-field
                v-model="sendCc"
                type="email"
                label="CC:"
                required
                :rules="[v => !!v || 'Email is required!']"
              ></v-text-field>
            </div>
            <v-text-field
              v-model="sendSubject"
              type="text"
              label="Subject:"
              :rules="[v => !!v || 'Subject is required!']"
              counter="114"
            ></v-text-field>
            <v-divider></v-divider>
            <v-card-actions>
              <v-flex xs5>
                <vonap-toolbar-label label="Email Signature">
                  <kendo-dropdownlist
                    v-model="emailSignature"
                    :data-source="analystEmailsDataSource"
                    :data-text-field="'email'"
                    :data-value-field="'email'"
                    style="width:150px"
                  >
                  </kendo-dropdownlist>
                </vonap-toolbar-label>
              </v-flex>
              <v-spacer></v-spacer>
              <vonap-toolbar-label>
                <v-switch v-model="sendInstructionsEmail" label="Send instructions Email" />
              </vonap-toolbar-label>
              <v-btn
                small
                icon
                color="blue primary"
                @click="saveEmailParams"
                style="width:32px;height:32px"
              >
                <v-icon>save</v-icon>
              </v-btn>
              <v-btn icon color="blue primary" @click="togglePreview">
                <v-icon style="font-size:16px">fa fa-binoculars</v-icon>
              </v-btn>
              <v-btn type="submit" color="blue primary" :disabled="sending">
                <v-progress-circular indeterminate v-if="sending" />
                Send
              </v-btn>
            </v-card-actions>
          </v-container>
        </v-card>
      </v-form>

      <draw-down-email-preview
        v-model="drawDownEmailPreviewVisible"
        :form-params="actualDrawDownParams"
        @success="drawDownEmailPreviewVisible = $event"
      />
    </v-layout>
  </vonap-window>
</template>
<script>
import axios from 'axios'
import {mapGetters} from 'vuex'
import VonapWindow from '@/components/VonapWindow'
import VonapToolbarLabel from '@/components/VonapToolbarLabel'
import DrawDownEmailPreview from '@/components/DrawDownEmailPreview.vue'
import confirm from '@/helpers/confirmation-window'
import {numericEditor} from '@/helpers/kendo-editors'

export default {
  name: 'DrawdownForm',
  components: {
    VonapWindow,
    VonapToolbarLabel,
    DrawDownEmailPreview,
  },
  props: {
    selectedOperations: {type: Array, default: null},
    value: {
      type: Boolean,
    },
    asOfDate: {
      type: String,
    },
  },
  async mounted() {
    const {data} = await axios.get('/api/report/drawDown/getDrawDownInformation', {
      params: {
        operations: this.selectedOperations,
      },
      paramsSerializer: params => {
        return Object.keys(params)
          .map(key => {
            if (Array.isArray(params[key])) {
              return params[key].map(value => `${key}=${value}`).join('&')
            }
            return `${key}=${params[key]}`
          })
          .join('&')
      },
    })

    this.clientId = data.clientId
    this.clientName = data.clientName
    this.sendFrom = data.fromEmail
    this.sendTo = data.toEmail
    this.sendSubject = data.subject
    this.templateId = data.templateId
    this.emailSignature = data.emailSignature
    this.dealDataSource.data(data.drawDownItems)
    if (data.warningMessage) {
      kendo.alert(data.warningMessage)
    }

    if (data.errorMessage) {
      kendo.alert(data.errorMessage)
      this.close()
    }
  },
  data() {
    return {
      sendInstructionsEmail: true,
      dealDataSource: new kendo.data.DataSource({
        data: [
          // sample data to bind
        ],
        schema: {
          model: {
            id: 'operationId',
            fields: {
              operationId: {type: 'number', editable: false},
              description: {type: 'string', editable: false},
              amount: {type: 'number', editable: true},
              complete: {type: 'boolean', editable: true},
            },
          },
        },
      }),
      templateId: 1,
      templateDataSource: [{text: 'English', value: 1}, {text: 'French', value: 2}],
      operationModel: {},
      sending: false,
      clientId: 0,
      clientName: 'ABIPA CANADA',
      clientEmail: '',
      sendFrom: 'trading@finmetrix.com',
      sendTo: 'confirmations@bnc.ca',
      sendCc: 'trading2@finmetrix.com',
      sendingMessageId: 0,
      sendingMessageName: 'N/A',
      sendingMessageData: new kendo.data.DataSource({
        transport: {
          read: {
            url: '/api/report/ExposureReport/getSendingMessageData',
            data: () => {
              return {clientId: +this.formParams.clientId}
            },
            contentType: 'application/json',
            type: 'GET',
          },
        },
      }),
      sendSubject: 'DRAWDOWN',
      sendComments: '',
      optionValue: 0,
      optionDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            url: '/api/report/ExposureReport/getForwardOption',
            contentType: 'application/json',
            type: 'GET',
          },
        },
      }),
      showTransactionsPanel: true,
      changeSendingMessageVisible: false,
      testMode: false,
      drawDownEmailPreviewVisible: false,
      actualDrawDownParams: {},
      emailSignature: '',
      analystEmailsDataSource: new kendo.data.DataSource({
        transport: {
          read: {
            url: '/api/report/ExposureReport/getAnalystEmails',
            contentType: 'application/json',
            type: 'GET',
          },
        },
      }),
    }
  },
  methods: {
    async sendMail() {
      if (!this.$refs.form.validate()) return
      const allDrawDownAmounts = this.dealDataSource
        .data()
        .every(x => x.amount != null && x.amount != 0)
      if (!allDrawDownAmounts) {
        kendo.alert('Please enter drawdown amounts for all operations')
        return
      }
      const confirmMessage = this.sendInstructionsEmail
        ? 'Are you sure you want to send the drawdown email?'
        : 'Execute the drawdown without sending the instructions email?'
      confirm.openConfirmationWindow('Confirmation', confirmMessage, this.performSend)
    },
    async performSend() {
      const formParams = this.getFormParams()
      const {data} = await axios.post('/api/report/DrawDown/sendEmail', formParams)
      if (data.status != 200) {
        kendo.alert(data.message)
        return
      }
      this.$emit('mailSent')
      this.close()
    },
    togglePreview() {
      this.actualDrawDownParams = this.getFormParams()
      this.drawDownEmailPreviewVisible = !this.drawDownEmailPreviewVisible
    },
    onSendFromChange() {},
    getFormParams() {
      return {
        clientId: this.clientId,
        clientName: this.clientName,
        fromEmail: this.sendFrom,
        toEmail: this.sendTo,
        ccEmail: this.sendCc,
        subject: this.sendSubject,
        drawDownDate: this.asOfDate,
        drawDownItems: this.dealDataSource.data(),
        templateId: this.templateId,
        emailSignature: this.emailSignature,
        sendInstructionsEmail: this.sendInstructionsEmail,
      }
    },
    async saveEmailParams() {
      const formParams = this.getFormParams()
      /*const {data} = */
      await axios.post('/api/report/DrawDown/saveEmailParams', formParams)
    },
    onTestModeChange() {},
    fullPartialTemplate(dataItem) {
      return dataItem.FullPartial ? 'Full' : 'Partial'
    },
    refresh() {
      this.$emit('success')
    },
    close() {
      this.$emit('input', false)
    },
    async drawdown() {
      const response = await axios.post('/api/list/deal/drawdownOperations', {
        operations: this.selectedOperations,
        deliveryDate: this.asOfDate,
        t: new Date().getTime(),
      })
      if (response.status == 200) {
        this.refresh()
      }
    },
    onSave(e) {
      if (e.values['amount']) {
        this.$nextTick(() => {
          e.sender.refresh()
        })
      }
    },
    amountTemplate(dataItem) {
      if (dataItem.amount === 0) {
        return ''
      } else {
        return kendo.toString(dataItem.amount, 'n0')
      }
    },
    amountEditor(container, options) {
      return numericEditor(
        {
          min: 0,
          max: options.model.leftToSettle,
          spinners: false,
        },
        false,
      ).call(this, container, options)
    },
  },
  computed: {
    ...mapGetters({currentRole: 'authentication/currentRole'}),
    ...mapGetters({tokenString: 'authentication/tokenString'}),

    //loadData() {
    //  return this.getDeliverySchedule()
    //},
  },
}
</script>
